// League workspace — Teams Management view.
//
// Registered-teams ledger + a modal Register/Edit form. Approvals go
// straight to backend. Extracted verbatim from the old League.jsx tab
// — same data flow, same modal patterns, just its own file.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, ShieldCheck, ShieldQuestion,
  CheckCircle2, XCircle, Mail, UserRound, Trophy, LayoutGrid, Layers,
  GripVertical, Sparkles
} from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import Avatar from "../../components/Avatar";
import { api } from "../../api";
import { fmtInstantEastern } from "../../utils/time";
import { EMPTY_TEAM, TEAM_TYPE_META, STATUS_META } from "./shared";
import LeagueDivisions from "./LeagueDivisions";

const CARD_TONES = ["gold", "cyan", "green", "violet", "orange"];
function toneFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CARD_TONES[h % CARD_TONES.length];
}
function teamMonogram(name = "") {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase() || "?";
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
function sortByName(list) {
  return [...list].sort((a, b) =>
    (a.teamName || "").localeCompare(b.teamName || "", undefined, { sensitivity: "base" })
  );
}

export default function LeagueTeams({ user }) {
  const toast = useToast();
  const [teams,       setTeams]       = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [divisions,   setDivisions]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [busy,        setBusy]        = useState(false);
  const [viewMode,       setViewMode]       = useState("cards"); // "cards" | "structure" | "divisions"
  const [dragTeamId,     setDragTeamId]     = useState(null);
  const [dragOverId,     setDragOverId]     = useState(null);   // divisionId or "unassigned"
  const [dragColumnId,   setDragColumnId]   = useState(null);   // divisionId being reordered
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  // Ref mirrors of the drag ids so drag-over/drop handlers see the
  // current value even before React re-renders after a state update.
  // State stays around for visual styling (column shadows / dim).
  const dragColumnIdRef = useRef(null);
  const dragTeamIdRef   = useRef(null);

  const [showForm,      setShowForm]      = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [form,          setForm]          = useState(EMPTY_TEAM);

  async function refresh() {
    setLoading(true);
    try {
      const [t, opts, divs] = await Promise.all([
        api.listRegisteredTeams(), api.listTeamOptions(), api.listDivisions()
      ]);
      setTeams(t);
      setTeamOptions(opts);
      setDivisions(divs);
    } catch (err) {
      toast.error(err.message || "Couldn't load teams.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // ── Drag & drop helpers — team cards ───────────────────────────
  function onDragStart(e, teamId) {
    dragTeamIdRef.current   = teamId;
    dragColumnIdRef.current = null;
    setDragTeamId(teamId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(teamId)); } catch { /* ignore */ }
  }
  function onDragEnd() {
    dragTeamIdRef.current = null;
    setDragTeamId(null); setDragOverId(null);
  }
  function onDragOver(e, laneId) {
    // Ignore team-lane hovers when a column reorder is in progress.
    if (dragColumnIdRef.current != null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== laneId) setDragOverId(laneId);
  }
  function onDragLeave(laneId) {
    if (dragOverId === laneId) setDragOverId(null);
  }

  // ── Drag & drop helpers — division column reorder ──────────────
  function onColumnDragStart(e, divisionId) {
    dragColumnIdRef.current = divisionId;
    dragTeamIdRef.current   = null;
    setDragColumnId(divisionId);
    setDragTeamId(null); // safety: don't leave a stale team drag flagged
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("application/x-division", String(divisionId)); } catch { /* ignore */ }
  }
  function onColumnDragEnd() {
    dragColumnIdRef.current = null;
    setDragColumnId(null); setDragOverColumnId(null);
  }
  function onColumnDragOver(e, targetDivisionId) {
    if (dragColumnIdRef.current == null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColumnId !== targetDivisionId) setDragOverColumnId(targetDivisionId);
  }
  async function onColumnDrop(e, targetDivisionId) {
    const draggedId = dragColumnIdRef.current;
    if (draggedId == null) return;
    e.preventDefault();
    e.stopPropagation();
    dragColumnIdRef.current = null;
    setDragColumnId(null); setDragOverColumnId(null);
    if (draggedId === targetDivisionId) return;

    const currentOrder = divisions;
    const from = currentOrder.findIndex(d => d.id === draggedId);
    if (from < 0) return;

    const next = [...currentOrder];
    const [moved] = next.splice(from, 1);
    // Insert dragged column BEFORE the target so the drop feels
    // like "slot it in here."
    const insertAt = next.findIndex(d => d.id === targetDivisionId);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);

    setDivisions(next); // optimistic
    try {
      await api.reorderDivisions(next.map(d => d.id));
      // Fresh fetch so any concurrent edits reconcile.
      refresh();
    } catch (err) {
      toast.error(err.message || "Couldn't save the new order.");
      refresh();
    }
  }
  async function onDrop(e, targetDivisionId) {
    e.preventDefault();
    const teamId = dragTeamId ?? Number(e.dataTransfer.getData("text/plain"));
    setDragOverId(null); setDragTeamId(null);
    if (!Number.isFinite(teamId)) return;
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    const currentDiv = team.divisionId == null ? null : Number(team.divisionId);
    const nextDiv    = targetDivisionId == null ? null : Number(targetDivisionId);
    if (currentDiv === nextDiv) return;
    // Optimistic update — snap the card into place before the network round trip.
    setTeams(prev => prev.map(t => t.id === teamId
      ? { ...t, divisionId: nextDiv, divisionName: nextDiv == null
          ? null : divisions.find(d => d.id === nextDiv)?.name }
      : t
    ));
    try {
      await api.setTeamDivision(teamId, nextDiv);
      toast.success(nextDiv == null
        ? `Moved ${team.teamName} to Unassigned.`
        : `Moved ${team.teamName} into ${divisions.find(d => d.id === nextDiv)?.name}.`);
      refresh();
    } catch (err) {
      toast.error(err.message || "Couldn't move team.");
      refresh();
    }
  }

  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm]);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_TEAM);
    setShowForm(true);
  }
  function openEdit(t) {
    setEditingId(t.id);
    setForm({
      teamType:         t.teamType,
      teamName:         t.teamName || "",
      existingTeamId:   t.existingTeamId != null ? String(t.existingTeamId) : "",
      captainFirstName: t.captainFirstName || "",
      captainLastName:  t.captainLastName  || "",
      captainEmail:     t.captainEmail     || "",
      notes:            t.notes            || ""
    });
    setShowForm(true);
  }
  function close() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_TEAM);
  }

  function onExistingTeamChange(id) {
    const match = teamOptions.find(o => String(o.id) === String(id));
    setForm(f => ({
      ...f,
      existingTeamId: id,
      teamName: match && (f.teamType === "Existing" || !f.teamName)
        ? match.name : f.teamName
    }));
  }
  function onTeamTypeChange(type) {
    setForm(f => {
      const next = { ...f, teamType: type };
      if (type === "New") next.existingTeamId = "";
      if (type === "Existing" && f.existingTeamId) {
        const m = teamOptions.find(o => String(o.id) === String(f.existingTeamId));
        if (m) next.teamName = m.name;
      }
      return next;
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.teamName.trim())         { toast.error("Team name is required."); return; }
    if (!form.captainFirstName.trim()) { toast.error("Captain's first name is required."); return; }
    if (!form.captainEmail.trim())     { toast.error("Captain's email is required."); return; }
    if ((form.teamType === "Existing" || form.teamType === "ExistingRenamed") && !form.existingTeamId) {
      toast.error("Select the existing team this registration is for."); return;
    }
    setBusy(true);
    try {
      const payload = {
        teamType:         form.teamType,
        teamName:         form.teamName.trim(),
        existingTeamId:   form.existingTeamId || null,
        captainFirstName: form.captainFirstName.trim(),
        captainLastName:  form.captainLastName.trim(),
        captainEmail:     form.captainEmail.trim(),
        notes:            form.notes.trim()
      };
      if (editingId) {
        await api.updateRegisteredTeam(editingId, payload);
        toast.success("Registration updated.");
      } else {
        await api.registerLeagueTeam(payload);
        toast.success("Team registered.");
      }
      close();
      await refresh();
    } catch (err) {
      toast.error(err.message || "Registration failed.");
    } finally { setBusy(false); }
  }

  async function setStatus(t, status) {
    try {
      await api.setRegisteredTeamStatus(t.id, status);
      toast.success(`Registration marked ${status.toLowerCase()}.`);
      await refresh();
    } catch (err) { toast.error(err.message || "Couldn't update status."); }
  }

  async function remove(t) {
    if (!confirm(`Remove "${t.teamName}" from the registration list?`)) return;
    try {
      await api.deleteRegisteredTeam(t.id);
      toast.success("Removed.");
      await refresh();
    } catch (err) { toast.error(err.message || "Couldn't remove."); }
  }

  const stats = useMemo(() => ({
    pending:  teams.filter(t => t.status === "Pending").length,
    approved: teams.filter(t => t.status === "Approved").length,
    rejected: teams.filter(t => t.status === "Rejected").length
  }), [teams]);

  return (
    <div className="stack league-view">
      <div className="builderTop">
        <div>
          <p>Pittsburgh Cricket League</p>
          <h2>Teams Management</h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {viewMode === "cards" && (
            <button className="btn" onClick={openCreate}>
              <Plus size={16}/> Register Team
            </button>
          )}
        </div>
      </div>

      {/* View-mode toggle — Cards · Division Structure (kanban) · Divisions (season structure) */}
      <div className="teams-viewToggle" role="tablist" aria-label="Teams view mode">
        <button
          type="button"
          role="tab"
          aria-selected={viewMode === "cards"}
          className={`teams-viewToggle-btn${viewMode === "cards" ? " teams-viewToggle-btn--active" : ""}`}
          onClick={() => setViewMode("cards")}
        >
          <LayoutGrid size={13}/> Team Cards
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewMode === "structure"}
          className={`teams-viewToggle-btn${viewMode === "structure" ? " teams-viewToggle-btn--active" : ""}`}
          onClick={() => setViewMode("structure")}
        >
          <Layers size={13}/> Division Structure
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewMode === "divisions"}
          className={`teams-viewToggle-btn${viewMode === "divisions" ? " teams-viewToggle-btn--active" : ""}`}
          onClick={() => setViewMode("divisions")}
        >
          <Sparkles size={13}/> Divisions
        </button>
      </div>

      {viewMode === "divisions" && <LeagueDivisions user={user} embedded/>}

      {viewMode === "structure" && (
        <StructureView
          teams={teams}
          divisions={divisions}
          loading={loading}
          dragTeamId={dragTeamId}
          dragOverId={dragOverId}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          dragColumnId={dragColumnId}
          dragOverColumnId={dragOverColumnId}
          onColumnDragStart={onColumnDragStart}
          onColumnDragEnd={onColumnDragEnd}
          onColumnDragOver={onColumnDragOver}
          onColumnDrop={onColumnDrop}
        />
      )}

      {viewMode === "cards" && (
      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1}}>
          <ShieldCheck size={18}/>
          <span>Registered Teams</span>
          <div className="league-teamCounts">
            <span className="league-teamCount league-teamCount--gold"  title="Pending">
              {stats.pending} pending
            </span>
            <span className="league-teamCount league-teamCount--green" title="Approved">
              {stats.approved} approved
            </span>
            {stats.rejected > 0 && (
              <span className="league-teamCount league-teamCount--red" title="Rejected">
                {stats.rejected} rejected
              </span>
            )}
          </div>
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && teams.length === 0 && (
          <div className="league-annEmpty">
            <ShieldCheck size={28}/>
            <p>No teams registered yet. Use <b>Register Team</b> to add the first one.</p>
          </div>
        )}

        {!loading && teams.length > 0 && (() => {
          const renderCard = t => {
            const typeMeta   = TEAM_TYPE_META[t.teamType] || TEAM_TYPE_META.New;
            const statusMeta = STATUS_META[t.status]      || STATUS_META.Pending;
            return (
              <article key={t.id} className={`league-teamCard league-teamCard--${statusMeta.tone}`}>
                <header className="league-teamCard-head">
                  <div className="league-teamCard-headLeft">
                    <span className={`league-teamCard-typePill league-teamCard-typePill--${typeMeta.tone}`}>
                      <Trophy size={11}/> {typeMeta.label}
                    </span>
                    <span className={`league-teamCard-statusPill league-teamCard-statusPill--${statusMeta.tone}`}>
                      {t.status === "Approved" ? <CheckCircle2 size={11}/>
                        : t.status === "Rejected" ? <XCircle size={11}/>
                        : <ShieldQuestion size={11}/>}
                      {statusMeta.label}
                    </span>
                  </div>
                  <div className="league-teamCard-actions">
                    {t.status !== "Approved" && (
                      <button type="button" className="league-teamCard-iconBtn league-teamCard-iconBtn--approve"
                        onClick={() => setStatus(t, "Approved")} title="Approve">
                        <CheckCircle2 size={14}/>
                      </button>
                    )}
                    {t.status !== "Rejected" && (
                      <button type="button" className="league-teamCard-iconBtn league-teamCard-iconBtn--reject"
                        onClick={() => setStatus(t, "Rejected")} title="Reject">
                        <XCircle size={14}/>
                      </button>
                    )}
                    <button type="button" className="league-teamCard-iconBtn"
                      onClick={() => openEdit(t)} title="Edit">
                      <Pencil size={14}/>
                    </button>
                    <button type="button" className="league-teamCard-iconBtn league-teamCard-iconBtn--danger"
                      onClick={() => remove(t)} title="Remove">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </header>

                <h3 className="league-teamCard-name">{t.teamName}</h3>
                {t.teamType === "ExistingRenamed" && t.existingTeamName && (
                  <div className="league-teamCard-renamedNote">
                    formerly <b>{t.existingTeamName}</b>
                  </div>
                )}
                {t.notes && <p className="league-teamCard-notes">{t.notes}</p>}

                <div className="league-teamCard-captain">
                  <Avatar
                    member={{
                      id: t.captainUserId,
                      name: (t.captainDisplayName || `${t.captainFirstName} ${t.captainLastName || ""}`.trim()),
                      avatarUpdatedAt: t.captainAvatarUpdatedAt
                    }}
                    size={36}
                  />
                  <div className="league-teamCard-captainInfo">
                    <b><UserRound size={12}/> Captain · {t.captainFirstName}{t.captainLastName ? ` ${t.captainLastName}` : ""}</b>
                    <small><Mail size={11}/> {t.captainEmail}</small>
                  </div>
                </div>

                <footer className="league-teamCard-foot">
                  <span>Registered by <b>{t.registeredBy || "—"}</b></span>
                  <span className="league-teamCard-when">· {fmtInstantEastern(t.registeredAt)}</span>
                </footer>
              </article>
            );
          };

          // Group by division in SortOrder from the API; Unassigned goes
          // at the end. Teams inside each group are alphabetical.
          const activeTeams = teams; // Rejected teams still show — admin needs to reject/unreject them
          const grouped = divisions.map(d => ({
            division: d,
            teams: sortByName(activeTeams.filter(t => t.divisionId === d.id))
          }));
          const unassigned = sortByName(activeTeams.filter(t => t.divisionId == null));

          return (
            <div className="teams-cards-groups">
              {grouped.map(({ division, teams: list }) => {
                const isFull = list.length >= division.maxTeams;
                return (
                  <section
                    key={division.id}
                    className={`teams-cards-group teams-cards-group--${toneFor(division.name)}`}
                  >
                    <header className="teams-cards-groupHead">
                      <div className="teams-cards-groupIcon">
                        <Layers size={22}/>
                      </div>
                      <div className="teams-cards-groupTitle">
                        <span className="teams-cards-groupEyebrow">
                          <Trophy size={10}/> {division.season}
                        </span>
                        <h3>{division.name}</h3>
                      </div>
                      <div className="teams-cards-groupMeta">
                        <div className="teams-cards-groupMetaCount">
                          <b>{list.length}</b>
                          <span>/ {division.maxTeams} teams</span>
                        </div>
                        {isFull && (
                          <span className="teams-cards-groupBadge">Full</span>
                        )}
                      </div>
                    </header>
                    {list.length === 0 ? (
                      <p className="teams-cards-groupEmpty">
                        No teams in this division yet. Use <b>Division Structure</b> to drag teams in.
                      </p>
                    ) : (
                      <div className="league-teamList">{list.map(renderCard)}</div>
                    )}
                  </section>
                );
              })}

              {/* Unassigned at the end, only if there's anything there */}
              {unassigned.length > 0 && (
                <section className="teams-cards-group teams-cards-group--unassigned">
                  <header className="teams-cards-groupHead">
                    <div className="teams-cards-groupIcon">
                      <ShieldQuestion size={22}/>
                    </div>
                    <div className="teams-cards-groupTitle">
                      <span className="teams-cards-groupEyebrow">Awaiting a division</span>
                      <h3>Unassigned</h3>
                    </div>
                    <div className="teams-cards-groupMeta">
                      <div className="teams-cards-groupMetaCount">
                        <b>{unassigned.length}</b>
                        <span>team{unassigned.length === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                  </header>
                  <div className="league-teamList">{unassigned.map(renderCard)}</div>
                </section>
              )}
            </div>
          );
        })()}
      </Panel>
      )}

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Team Registration" : "Register a Team"}>
              <form className="form" onSubmit={submit}>
                <label>Team Type *</label>
                <div className="league-typeChoice">
                  {Object.entries(TEAM_TYPE_META).map(([code, meta]) => {
                    const active = form.teamType === code;
                    return (
                      <button
                        type="button"
                        key={code}
                        className={`league-typeChoice-btn${active ? " league-typeChoice-btn--active" : ""} league-typeChoice-btn--${meta.tone}`}
                        onClick={() => onTeamTypeChange(code)}
                      >
                        <b>{meta.label}</b>
                        <small>{meta.hint}</small>
                      </button>
                    );
                  })}
                </div>

                {(form.teamType === "Existing" || form.teamType === "ExistingRenamed") && (
                  <>
                    <label>Existing Team *</label>
                    <select value={form.existingTeamId} onChange={e => onExistingTeamChange(e.target.value)}>
                      <option value="">Select an existing team…</option>
                      {teamOptions.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.name}{o.sportType ? ` · ${o.sportType}` : ""}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <label>
                  Team Name *
                  {form.teamType === "ExistingRenamed" && (
                    <em style={{fontStyle:"normal", color:"var(--muted)", marginLeft:8, textTransform:"none", letterSpacing:0}}>
                      — the new name for this season
                    </em>
                  )}
                </label>
                <input
                  value={form.teamName}
                  onChange={e => setForm({ ...form, teamName: e.target.value })}
                  placeholder={form.teamType === "New"
                    ? "e.g. Steel City Strikers"
                    : form.teamType === "ExistingRenamed"
                    ? "New name for this season"
                    : "Team display name"}
                />

                <div className="two">
                  <div>
                    <label>Captain's First Name *</label>
                    <input value={form.captainFirstName}
                      onChange={e => setForm({ ...form, captainFirstName: e.target.value })}
                      placeholder="First name"/>
                  </div>
                  <div>
                    <label>Captain's Last Name</label>
                    <input value={form.captainLastName}
                      onChange={e => setForm({ ...form, captainLastName: e.target.value })}
                      placeholder="Last name"/>
                  </div>
                </div>

                <label>Captain's Email *</label>
                <input type="email" value={form.captainEmail}
                  onChange={e => setForm({ ...form, captainEmail: e.target.value })}
                  placeholder="captain@example.com"/>

                <label>Notes</label>
                <textarea rows={3} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional — internal notes visible to the committee."/>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Register Team"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

// ── StructureView ──────────────────────────────────────────────────
// Kanban-style drag-and-drop board grouping teams into their
// divisions. First column is always the "Unassigned" pool; every
// other column is one division. Team cards are draggable pills;
// divisions are drop targets.
function StructureView({
  teams, divisions, loading,
  dragTeamId, dragOverId,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  dragColumnId, dragOverColumnId,
  onColumnDragStart, onColumnDragEnd, onColumnDragOver, onColumnDrop
}) {
  // Rejected teams stay out of the drag board so the picture stays
  // focused on active season structure.
  const activeTeams = teams.filter(t => t.status !== "Rejected");
  const unassigned  = sortByName(activeTeams.filter(t => t.divisionId == null));
  const teamsByDiv  = new Map();
  for (const t of activeTeams) {
    if (t.divisionId == null) continue;
    if (!teamsByDiv.has(t.divisionId)) teamsByDiv.set(t.divisionId, []);
    teamsByDiv.get(t.divisionId).push(t);
  }

  return (
    <Panel title={
      <div style={{display:"flex", alignItems:"center", gap:10, flex:1}}>
        <Layers size={18}/>
        <span>Division Structure</span>
        <em style={{marginLeft:"auto", color:"var(--muted)", fontStyle:"normal", fontSize:12}}>
          Drag the grip <GripVertical size={11} style={{verticalAlign:"middle"}}/> to reorder divisions · drag team pills between columns
        </em>
      </div>
    }>
      {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
      {!loading && divisions.length === 0 && (
        <div className="league-annEmpty">
          <Layers size={28}/>
          <p>
            No divisions yet. Open the <b>Divisions</b> tab to create your
            season structure first.
          </p>
        </div>
      )}

      {!loading && divisions.length > 0 && (
        <div className="teams-kanban">
          {divisions.map(d => {
            const list = sortByName(teamsByDiv.get(d.id) || []);
            const isOver = dragOverId === d.id;
            const isColOver = dragOverColumnId === d.id && dragColumnId != null && dragColumnId !== d.id;
            const isDragging = dragColumnId === d.id;
            const tone   = toneFor(d.name);
            const isFull = list.length >= d.maxTeams;
            const pct    = Math.min(100, Math.round((list.length / d.maxTeams) * 100));
            return (
              <section
                key={d.id}
                className={
                  `teams-kanban-col teams-kanban-col--${tone}` +
                  (isOver ? " teams-kanban-col--over" : "") +
                  (isFull ? " teams-kanban-col--full" : "") +
                  (isColOver ? " teams-kanban-col--colOver" : "") +
                  (isDragging ? " teams-kanban-col--dragging" : "")
                }
                onDragOver={e => {
                  // onColumnDragOver preventDefaults when a column is
                  // being dragged; otherwise onDragOver preventDefaults
                  // for a team-card drop. Only one bails, one wins.
                  onColumnDragOver(e, d.id);
                  onDragOver(e, d.id);
                }}
                onDragLeave={() => onDragLeave(d.id)}
                onDrop={e => {
                  // Column drop takes precedence — the internal ref check
                  // makes this decision even before React re-renders.
                  onColumnDrop(e, d.id);
                  onDrop(e, d.id);
                }}
              >
                <header className="teams-kanban-colHead">
                  {/* Drag handle — a <span> because <button draggable>
                      is unreliable in Firefox and older Safari.
                      onMouseDown stopper keeps the click from bubbling
                      to nested draggable team cards. */}
                  <span
                    role="button"
                    tabIndex={0}
                    className="teams-kanban-grip"
                    draggable="true"
                    onMouseDown={e => e.stopPropagation()}
                    onDragStart={e => onColumnDragStart(e, d.id)}
                    onDragEnd={onColumnDragEnd}
                    title="Drag to reorder division"
                    aria-label="Reorder division"
                  >
                    <GripVertical size={14}/>
                  </span>
                  <div>
                    <small>{d.season}</small>
                    <span className="teams-kanban-colTitle">{d.name}</span>
                  </div>
                  <span className="teams-kanban-colCount">
                    {list.length} <em>/ {d.maxTeams}</em>
                  </span>
                </header>
                <div className="teams-kanban-colBar">
                  <div className="teams-kanban-colBar-fill" style={{width: `${pct}%`}}/>
                </div>
                <div className="teams-kanban-drop">
                  {list.length === 0 && (
                    <p className="teams-kanban-empty">Drop teams here.</p>
                  )}
                  {list.map(t => (
                    <TeamDragCard
                      key={t.id}
                      team={t}
                      dragging={dragTeamId === t.id}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                    />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Unassigned lives at the far right so the numbered divisions
              read left-to-right before the "everything else" bucket. */}
          <section
            className={`teams-kanban-col teams-kanban-col--unassigned${dragOverId === "unassigned" ? " teams-kanban-col--over" : ""}`}
            onDragOver={e => onDragOver(e, "unassigned")}
            onDragLeave={() => onDragLeave("unassigned")}
            onDrop={e => onDrop(e, null)}
          >
            <header className="teams-kanban-colHead">
              <span className="teams-kanban-colTitle">Unassigned</span>
              <span className="teams-kanban-colCount">{unassigned.length}</span>
            </header>
            <div className="teams-kanban-drop">
              {unassigned.length === 0 && (
                <p className="teams-kanban-empty">
                  All teams assigned. Drop teams here to remove them from a division.
                </p>
              )}
              {unassigned.map(t => (
                <TeamDragCard
                  key={t.id}
                  team={t}
                  dragging={dragTeamId === t.id}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                />
              ))}
            </div>
          </section>
        </div>
      )}
    </Panel>
  );
}

function TeamDragCard({ team, dragging, onDragStart, onDragEnd }) {
  const tone = toneFor(team.teamName);
  const statusMeta = STATUS_META[team.status] || STATUS_META.Pending;
  return (
    <article
      className={`teams-kanban-card teams-kanban-card--${tone}${dragging ? " teams-kanban-card--dragging" : ""}`}
      draggable="true"
      onDragStart={e => onDragStart(e, team.id)}
      onDragEnd={onDragEnd}
    >
      <div className="teams-kanban-card-monogram">{teamMonogram(team.teamName)}</div>
      <div className="teams-kanban-card-body">
        <b>{team.teamName}</b>
        <small>
          Captain · {team.captainFirstName}
          {team.captainLastName ? ` ${team.captainLastName}` : ""}
        </small>
      </div>
      <span className={`teams-kanban-card-status teams-kanban-card-status--${statusMeta.tone}`}>
        {team.status}
      </span>
    </article>
  );
}
